import { useEffect, useRef, useState, type ReactNode } from "react";

import { useI18n } from "~/i18n";
import { cn } from "~/lib/utils";
import { OrganizationAvatar } from "../org/organizationShared";
import { Card } from "../ui/card";
import { MorrowPersonAvatar, personRoleLine } from "./MorrowPersonCard";

type IdentityMetaRow = {
  readonly label: string;
  readonly value: string;
  readonly href?: string;
};

function findScrollParent(node: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = node.parentElement;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === "auto" || overflowY === "scroll") return current;
    current = current.parentElement;
  }
  return null;
}

function useIdentityCardScrollMotion() {
  const ref = useRef<HTMLDivElement>(null);
  const [translateY, setTranslateY] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const scrollEl = findScrollParent(node);
    if (!scrollEl) return;

    const baseScrollTop = scrollEl.scrollTop;
    let frame = 0;

    const sync = () => {
      frame = 0;
      const delta = scrollEl.scrollTop - baseScrollTop;
      setTranslateY(delta * 0.2);
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(sync);
    };

    scrollEl.addEventListener("scroll", onScroll, { passive: true });
    sync();
    return () => {
      scrollEl.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return { ref, translateY };
}

function MorrowIdentityCardShell({
  badge,
  portrait,
  name,
  subtitle,
  meta,
  description,
  className,
}: {
  readonly badge: string;
  readonly portrait: ReactNode;
  readonly name: string;
  readonly subtitle?: string;
  readonly meta?: ReadonlyArray<IdentityMetaRow>;
  readonly description?: string;
  readonly className?: string;
}) {
  const { ref, translateY } = useIdentityCardScrollMotion();
  const trimmedName = name.trim();
  const trimmedSubtitle = subtitle?.trim() ?? "";
  const trimmedDescription = description?.trim() ?? "";
  const rows = (meta ?? []).filter((row) => row.value.trim().length > 0);

  return (
    <div
      ref={ref}
      style={translateY !== 0 ? { transform: `translateY(${translateY}px)` } : undefined}
      className={cn("order-first motion-reduce:transform-none lg:order-none", className)}
    >
      <Card className="overflow-hidden border-border/60 bg-card/80 p-0 shadow-sm">
        <div className="flex flex-col items-center gap-4 border-b border-border/60 bg-muted/20 px-5 pb-5 pt-6 text-center">
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {badge}
          </p>
          {portrait}
          <div className="flex min-w-0 w-full flex-col gap-1">
            <h2 className="truncate text-base font-semibold tracking-tight text-foreground">
              {trimmedName}
            </h2>
            {trimmedSubtitle ? (
              <p className="text-sm leading-5 text-muted-foreground">{trimmedSubtitle}</p>
            ) : null}
          </div>
        </div>
        {rows.length > 0 ? (
          <dl className="flex flex-col gap-3 px-5 py-4">
            {rows.map((row) => (
              <div key={row.label} className="flex min-w-0 flex-col gap-0.5">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {row.label}
                </dt>
                <dd className="text-sm leading-5 text-foreground">
                  {row.href ? (
                    <a
                      href={row.href}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-primary underline-offset-4 hover:underline"
                    >
                      {row.value}
                    </a>
                  ) : (
                    row.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        {trimmedDescription ? (
          <p className="border-t border-border/60 px-5 py-4 text-xs leading-5 text-muted-foreground">
            {trimmedDescription}
          </p>
        ) : null}
      </Card>
    </div>
  );
}

function formatMemberSince(value: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(value);
}

function websiteHref(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function MorrowPersonIdentityCard({
  name,
  title = "",
  role = "",
  email = "",
  memberSince,
  description,
  imageUrl,
  avatarSeed,
  className,
}: {
  readonly name: string;
  readonly title?: string;
  readonly role?: string;
  readonly email?: string;
  readonly memberSince?: Date;
  readonly description?: string;
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
  readonly className?: string;
}) {
  const { t, locale } = useI18n();
  const roleLine = personRoleLine(title, role);
  const meta: IdentityMetaRow[] = [];
  const trimmedTitle = title.trim();
  const trimmedRole = role.trim();

  if (trimmedTitle) {
    meta.push({ label: t("team.joinTitle"), value: trimmedTitle });
  }
  if (trimmedRole) {
    meta.push({ label: t("team.joinRole"), value: trimmedRole });
  }
  if (email.trim()) {
    meta.push({ label: t("identity.email"), value: email.trim() });
  }
  if (memberSince) {
    meta.push({
      label: t("identity.memberSince"),
      value: formatMemberSince(memberSince, locale),
    });
  }

  return (
    <MorrowIdentityCardShell
      badge={t("identity.personBadge")}
      name={name}
      {...(roleLine ? { subtitle: roleLine } : {})}
      meta={meta}
      {...(description ? { description } : {})}
      {...(className ? { className } : {})}
      portrait={
        <MorrowPersonAvatar
          name={name}
          {...(imageUrl ? { imageUrl } : {})}
          {...(avatarSeed ? { avatarSeed } : {})}
          size="xl"
          className="size-24 rounded-2xl"
        />
      }
    />
  );
}

export function MorrowOrganizationIdentityCard({
  name,
  logoUrl = "",
  memberCount,
  industry = "",
  website = "",
  teamSize = "",
  goal = "",
  about = "",
  description,
  className,
}: {
  readonly name: string;
  readonly logoUrl?: string;
  readonly memberCount?: number;
  readonly industry?: string;
  readonly website?: string;
  readonly teamSize?: string;
  readonly goal?: string;
  readonly about?: string;
  readonly description?: string;
  readonly className?: string;
}) {
  const { t } = useI18n();
  const meta: IdentityMetaRow[] = [];
  const trimmedWebsite = website.trim();
  const websiteLink = websiteHref(trimmedWebsite);

  if (typeof memberCount === "number" && memberCount > 0) {
    meta.push({
      label: t("identity.members"),
      value: t("settings.organization.memberCount", { count: memberCount }),
    });
  }
  if (teamSize.trim()) {
    meta.push({ label: t("onboarding.profile.teamSize"), value: teamSize.trim() });
  }
  if (industry.trim()) {
    meta.push({ label: t("identity.industry"), value: industry.trim() });
  }
  if (goal.trim()) {
    meta.push({ label: t("onboarding.profile.goal"), value: goal.trim() });
  }
  if (trimmedWebsite) {
    meta.push({
      label: t("identity.website"),
      value: trimmedWebsite,
      ...(websiteLink ? { href: websiteLink } : {}),
    });
  }
  if (about.trim()) {
    meta.push({ label: t("identity.about"), value: about.trim() });
  }

  return (
    <MorrowIdentityCardShell
      badge={t("identity.organizationBadge")}
      name={name}
      meta={meta}
      {...(description ? { description } : {})}
      {...(className ? { className } : {})}
      portrait={
        logoUrl ? (
          <img
            alt=""
            src={logoUrl}
            className="size-24 shrink-0 rounded-2xl border border-border/60 object-cover"
          />
        ) : (
          <OrganizationAvatar
            logoUrl=""
            name={name}
            className="size-24 rounded-2xl border border-border/60 text-2xl"
          />
        )
      }
    />
  );
}
